"use client";

import {JSX, useState} from "react";

import Link from "next/link";

import SmartImage from "@/shared/ui/smart-image";

import type {HeaderDropdownItem} from "@/sections/common/header/parts/aditional-menus/header-dropdown";

import {ButtonSimple} from "@/shared/ui/buttons";
import {Typography} from "@/shared/ui/data-display/typography";
import {getStaticPathIcon} from "@/shared/utils/get-static-path-icon";

import "./styles/aditional-menu.scss";

export type AdditionalMenuHeaderLink = {
    id: number | string;
    href: string;
    label: string;
    dropdownItems?: Array<HeaderDropdownItem>;
};

type AdditionalMenuProps = {
    headerLinks: Array<AdditionalMenuHeaderLink>;
    additionalLinks: Array<{
        id: number | string;
        href: string;
        label: string;
        description?: string;
        icon?: string;
    }>;
    onClose?: () => void;
};

export function AdditionalMenu({headerLinks, additionalLinks, onClose}: AdditionalMenuProps): JSX.Element {
    const [expandedId, setExpandedId] = useState<number | string | null>(null);

    const toggleExpanded = (id: number | string): void => {
        setExpandedId((prev) => (prev === id ? null : id));
    };

    return (
        <div className="additional_menu">
            <button type="button" className="additional_menu__close" onClick={onClose} aria-label="Close menu">
                <span />
                <span />
            </button>

            <div className="additional_menu__mobile">
                {headerLinks.map((link) => {
                    const hasDropdown = link.dropdownItems && link.dropdownItems.length > 0;
                    const isExpanded = expandedId === link.id;

                    if (hasDropdown) {
                        return (
                            <div key={link.id} className="additional_menu__mobile-group">
                                <button
                                    type="button"
                                    className="additional_menu__mobile-item additional_menu__mobile-item--expandable"
                                    onClick={() => toggleExpanded(link.id)}
                                    aria-expanded={isExpanded}
                                    aria-haspopup="true"
                                >
                                    <span className="additional_menu__mobile-item-label">{link.label}</span>
                                    <span className={`additional_menu__mobile-item-chevron ${isExpanded ? "is-open" : ""}`} aria-hidden>
                                        <SmartImage src={getStaticPathIcon("chevron-down-booking")} alt="" width={16} height={16} />
                                    </span>
                                </button>
                                <div
                                    className={`additional_menu__mobile-sublinks-wrapper ${isExpanded ? "is-expanded" : ""}`}
                                    aria-hidden={!isExpanded}
                                >
                                    <div className="additional_menu__mobile-sublinks" role="group" aria-label={link.label}>
                                        <div className="additional_menu__mobile-sublinks-inner">
                                            {link.dropdownItems!.map((item) => (
                                                <ButtonSimple
                                                    as="link"
                                                    key={item.id}
                                                    href={item.href}
                                                    className="additional_menu__mobile-sublink"
                                                    variant="primary"
                                                    onClick={onClose}
                                                >
                                                    {item.label}
                                                </ButtonSimple>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    }

                    return (
                        <ButtonSimple as="link" key={link.id} href={link.href} variant="primary" onClick={onClose}>
                            {link.label}
                        </ButtonSimple>
                    );
                })}
            </div>
            <div className="additional_menu__big">
                {additionalLinks.map((link) => (
                    <Link href={link.href} className="additional_menu__big-item" key={link.id} onClick={onClose}>
                        {link.icon && (
                            <div className="additional_menu__big-icon">
                                <SmartImage src={getStaticPathIcon(link.icon)} alt={link.label} width={16} height={16} />
                            </div>
                        )}
                        <div className="additional_menu__big-content">
                            {link.label && (
                                <div className="additional_menu__big-title">
                                    <Typography as="div" size={12} color="black">
                                        {link.label}
                                    </Typography>
                                </div>
                            )}
                            {link.description && (
                                <div className="additional_menu__big-description">
                                    <Typography as="p" size={12} color="paragraph">
                                        {link.description}
                                    </Typography>
                                </div>
                            )}
                        </div>
                    </Link>
                ))}
            </div>
        </div>
    );
}
